import { AlertCircle, Loader2 } from 'lucide-react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import type { PatientEncounter } from '@/api/patientsApi'
import {
  ENCOUNTER_STATUS_PILL,
  encounterLocationLabel,
  encounterProviderName,
  formatEncounterMoment,
} from '@/lib/encounters'
import { cn } from '@/lib/utils'

/**
 * A patient's Epic visit history, as a table (z8nrz7e0rb).
 *
 * Shared by the two surfaces that show visits, both of them dialogs: the
 * Patient History dialog (`PatientHistoryPanel`) and the Patient Visit History
 * dialog reached from the patient detail page (`PatientVisitsDialog`). It lives
 * here rather than in either of them because a column added or a format
 * corrected must appear on both — the same rows read two ways is exactly the
 * disagreement `journey.ts` exists to prevent elsewhere in this app.
 *
 * READ-ONLY by design, and that is not incidental. An encounter is Epic's
 * record: nothing in this app may open, edit or link one, so the rows carry no
 * click target and no chevron. That is also what makes them read as a different
 * kind of thing from the procedures they sit beside.
 */

/**
 * The per-user column layout is keyed by this id, and BOTH surfaces pass it.
 *
 * Deliberately one preference rather than two: a coordinator who hides
 * `Account #` on the dialog means "I do not care about account numbers", not "I
 * do not care about them here". Sharing the id also means the layout saved
 * before this table was extracted still applies, so nobody's columns reset.
 */
export const ENCOUNTER_TABLE_ID = 'patient-history-encounters'

/**
 * `visit` is locked — the encounter number identifies the row, and it is the
 * only value a coordinator can quote back to the hospital. Account number and
 * the ADT provenance are secondary: useful when reconciling a delivery, noise
 * the rest of the time, so they start hidden.
 *
 * `attending` and `admitting` are two DIFFERENT people on the same visit —
 * PV1-7 and PV1-17 — and they sit next to each other so the pair reads as one
 * answer to "who". Often the same name arrives in both, and that is displayed
 * twice rather than collapsed: Epic saying the admitting doctor also attended
 * is a fact about the visit, not a duplicate to hide (z8nrz7e4mz).
 */
export const ENCOUNTER_COLUMNS: TableColumnDef[] = [
  { id: 'visit', header: 'Visit', locked: true, sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'patientClass', header: 'Class', sortable: true },
  { id: 'location', header: 'Location', sortable: true },
  { id: 'admitted', header: 'Admitted', sortable: true },
  { id: 'discharged', header: 'Discharged', sortable: true },
  { id: 'attending', header: 'Attending', sortable: true },
  { id: 'admitting', header: 'Admitting', sortable: true },
  { id: 'accountNumber', header: 'Account #', defaultHidden: true, sortable: true },
  { id: 'source', header: 'Source', defaultHidden: true, sortable: true },
]

// Column sorting (z8nrz7e4h6). Admit/discharge sort by the instant, not the
// precision-trimmed text; status by the pill a coordinator reads; both
// providers by the name on screen.
const ENCOUNTER_SORT: SortAccessors<PatientEncounter> = {
  visit: e => e.encounterNumber,
  status: e => ENCOUNTER_STATUS_PILL[e.status]?.label,
  patientClass: e => e.patientClass,
  location: e => encounterLocationLabel(e),
  admitted: e => e.admittedAt,
  discharged: e => e.dischargedAt,
  attending: e => encounterProviderName(e.attendingProvider),
  admitting: e => encounterProviderName(e.admittingProvider),
  accountNumber: e => e.accountNumber,
  source: e => (e.lastTrigger ? `${e.lastTrigger}${e.sourceSystem ? ` · ${e.sourceSystem}` : ''}` : null),
}

/**
 * Sticky header cells.
 *
 * On the `<th>`, not the `<thead>`: a sticky `thead` is only as tall as its cell
 * box, so a row scrolling underneath bled through above the column names. The
 * background and the bottom border therefore live on the cells, and `pt-3`
 * matches the body rows' `py-3` so the band is tall enough to cover one.
 */
const STICKY_TH = 'sticky top-0 z-10 border-b bg-background pb-2 pt-3'

export interface PatientVisitsTableProps {
  rows: PatientEncounter[] | undefined
  loading: boolean
  error: unknown
  /**
   * Scroll the TABLE and stick its header.
   *
   * True inside a height-capped dialog, where the table is the only thing that
   * may scroll — which is both callers today. False renders the table inline in
   * a page that scrolls as a whole, where a header pinned to the viewport top
   * would detach from the container it belongs to.
   */
  sticky?: boolean
}

export function PatientVisitsTable({ rows, loading, error, sticky = false }: PatientVisitsTableProps) {
  const cols = useTableColumns(ENCOUNTER_TABLE_ID, ENCOUNTER_COLUMNS)
  // Before the early returns: a hook cannot be conditional.
  const sortedRows = useSortedRows(rows, cols.sort, ENCOUNTER_SORT)

  if (error) {
    return (
      <State sticky={sticky}>
        <div className='flex max-w-sm flex-col items-center gap-2 text-center'>
          <AlertCircle className='h-5 w-5 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>
            Could not load visit history: {error instanceof Error ? error.message : 'unknown error'}
          </p>
        </div>
      </State>
    )
  }
  if (loading) {
    return (
      <State sticky={sticky}>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </State>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <State sticky={sticky}>
        <p className='text-center text-sm text-muted-foreground'>
          No hospital visits recorded for this patient. Visits appear here once Epic sends an
          admission (ADT) for this MRN.
        </p>
      </State>
    )
  }

  return (
    <div
      className={cn(
        'overflow-x-persistent',
        sticky ? 'min-h-0 flex-1 overflow-y-auto' : undefined,
      )}
    >
      <table className='w-full text-sm'>
        <thead>
          <tr className='text-left text-xs font-medium text-muted-foreground'>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                menuLabel='Visit columns'
                className={cn(
                  sticky ? STICKY_TH : 'border-b bg-table-header py-3 text-table-header-foreground',
                  sticky ? 'pr-4' : 'px-5',
                )}
              >
                {def.header}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(e => {
            const pill = ENCOUNTER_STATUS_PILL[e.status]
            const location = encounterLocationLabel(e)
            const attending = encounterProviderName(e.attendingProvider)
            const admitting = encounterProviderName(e.admittingProvider)
            // Rendered in UTC by `formatEncounterMoment`: the instant comes from
            // a ZONE-LESS HL7 wall clock, and the precision says how much of it
            // is real — a `day` admit's midnight is padding, not a time.
            const admitted = formatEncounterMoment(e.admittedAt, e.admittedAtPrecision)
            const discharged = formatEncounterMoment(e.dischargedAt, e.dischargedAtPrecision)
            const cell = sticky ? 'py-3 pr-4' : 'px-5 py-3'
            return (
              <tr key={e.id} className='border-b last:border-0'>
                <ColumnCells
                  columns={cols}
                  cells={{
                    visit: <td className={cn(cell, 'font-medium')}>{e.encounterNumber}</td>,
                    status: (
                      <td className={cell}>
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-medium',
                            pill.className,
                          )}
                        >
                          {pill.label}
                        </span>
                      </td>
                    ),
                    // Verbatim from PV1-2: `I`, `OUT` and `INPATIENT` are all
                    // values real senders use, so this is displayed, never mapped.
                    patientClass: <td className={cell}>{e.patientClass ?? <Dash />}</td>,
                    location: (
                      <td className={cn(cell, 'text-muted-foreground')}>{location ?? <Dash />}</td>
                    ),
                    admitted: (
                      <td className={cn(cell, 'text-muted-foreground')}>{admitted ?? <Dash />}</td>
                    ),
                    discharged: (
                      <td className={cn(cell, 'text-muted-foreground')}>{discharged ?? <Dash />}</td>
                    ),
                    attending: (
                      <td className={cn(cell, 'text-muted-foreground')}>{attending ?? <Dash />}</td>
                    ),
                    admitting: (
                      <td className={cn(cell, 'text-muted-foreground')}>{admitting ?? <Dash />}</td>
                    ),
                    accountNumber: (
                      <td className={cn(cell, 'text-muted-foreground')}>
                        {e.accountNumber ?? <Dash />}
                      </td>
                    ),
                    source: (
                      <td className={cn(cell, 'text-muted-foreground')}>
                        {e.lastTrigger ? (
                          <span title={e.sourceMessageId ?? undefined}>
                            {e.lastTrigger}
                            {e.sourceSystem ? ` · ${e.sourceSystem}` : ''}
                          </span>
                        ) : (
                          <Dash />
                        )}
                      </td>
                    ),
                  }}
                />
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Where a spinner, an error or the empty note goes.
 *
 * The dialog centres it in the space the table would have filled; the page card
 * cannot, because it has no fixed height to centre within — it just pads.
 */
function State({ sticky, children }: { sticky: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        sticky ? 'flex-1 py-8' : 'px-5 py-8',
      )}
    >
      {children}
    </div>
  )
}

/** An em dash for a field the message did not carry — never a blank cell. */
function Dash() {
  return <span className='text-muted-foreground/60'>—</span>
}
